/**
 * Settings -> Clips: the clip types and the words that find them. Core: Flow,
 * playlists and anything a plugin plays use the same types.
 *
 * Each type is a name and a list of words; Nova looks for the words (whole
 * words, any case, plural allowed) in the fields ticked below. The list order
 * is the order clips play in when several are due at once, and the first type
 * that matches a file wins -- drag to change it. Counts update while typing, so
 * a word that catches too much shows itself before it is saved.
 *
 * A file the words get wrong is fixed in its type column, when there is one
 * (Radio adds "Radio type"): a type's name, or "Song".
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { engineBase } from "../api";
import { useApp } from "../core/app";
import { moved, useDragReorder } from "../core/dragList";
import { Icon } from "../shell/Icons";
import { toast } from "../shell/Toast";
import { clockTime } from "../shell/Waveform";
import "./clips.css";

export interface ClipType {
  id: string;
  name: string;
  words: string[];
}

interface TypesPage {
  types: ClipType[];
  fields: string[];
  available: string[];
  changed: boolean;
  defaults: { types: ClipType[]; fields: string[] };
  column: string;
  /** The type column's name when the library has one (Radio adds it), else null. */
  columnLabel: string | null;
  song: string;
  error?: string;
}

interface Clip {
  trackId: string;
  title: string | null;
  artist: string | null;
  duration: number | null;
  type: string;
  why: string;
  byHand: boolean;
}

const FIELD_LABEL: Record<string, string> = {
  genre: "Genre",
  group: "Groups",
  title: "Title",
  comment: "Comment",
  folder: "Folder name",
};

const base = "/api/clips";

async function call<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${engineBase()}${base}/${path}`, body === undefined ? undefined : {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function ClipTypesSettings() {
  const app = useApp();
  const [page, setPage] = useState<TypesPage | null>(null);
  const [types, setTypes] = useState<ClipType[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  /** Files per type, in the order of the list on screen (saved or not). */
  const [counts, setCounts] = useState<number[] | null>(null);
  /** The row whose handle is held: only that row drags, so the text boxes stay usable. */
  const [armed, setArmed] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fresh = useRef(0);

  const take = (p: TypesPage) => {
    setPage(p);
    setTypes(p.types);
    setFields(p.fields);
  };
  useEffect(() => {
    if (app.offline) return;
    call<TypesPage>("types").then(take).catch((e) => setError(String(e)));
  }, [app.offline]);

  // Counts for the list as it stands on screen, saved or not.
  useEffect(() => {
    if (!page) return;
    const ticket = ++fresh.current;
    const timer = window.setTimeout(() => {
      call<{ ordered?: number[]; error?: string }>("classify", { types, fields })
        .then((r) => {
          if (ticket !== fresh.current) return;
          if (r.error) setError(r.error);
          else {
            setError(null);
            setCounts(r.ordered ?? []);
          }
        })
        .catch(() => undefined);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [page, types, fields]);

  const dirty = !!page && (!same(types, page.types) || !same(fields, page.fields));
  const atDefaults = !!page && same(types.map(({ name, words }) => ({ name, words })), page.defaults.types.map(({ name, words }) => ({ name, words }))) && same(fields, page.defaults.fields);
  const drag = useDragReorder({ onMove: (from, to) => setTypes((t) => moved(t, from, to)), canDrag: (i) => armed === i, canDrop: () => armed !== null });
  const savedIds = new Set(page?.types.map((t) => t.id));

  const edit = (i: number, change: Partial<ClipType>) => setTypes((t) => t.map((x, j) => (j === i ? { ...x, ...change } : x)));

  const save = async () => {
    setBusy(true);
    try {
      const r = await call<TypesPage>("types", { types, fields });
      if (r.error) {
        setError(r.error);
        return;
      }
      take(r);
      toast("Clip types saved. Flow's “Songs only” uses them too.", "ok");
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Put the clip types and fields back to Nova's defaults? Your own types and words are removed.")) return;
    take(await call<TypesPage>("types/reset", {}));
    toast("Clip types are back to the defaults.", "ok");
  };

  if (app.offline) return <p className="nv-dim">Start the engine to edit clip types.</p>;
  if (!page) return <p className="nv-dim">{error ?? "Reading…"}</p>;

  return (
    <div className="cl-types">
      <p className="nv-dim">
        What each kind of clip is called, and the words that mark a file as one. Nova looks for the words in the fields
        ticked below, as whole words in any case (“jingle” also finds “Jingles”). A file matching two types takes the
        first. When several clips are due at the same moment, they play in this order: drag to change it.
      </p>

      <ol className="cl-typelist">
        {types.map((t, i) => {
          const rp = drag.rowProps(i);
          const n = counts?.[i];
          return (
            <li key={`${t.id}-${i}`} {...rp} className={`cl-type ${rp.className}`} onDragEnd={() => { rp.onDragEnd(); setArmed(null); }}>
              <div className="cl-type-head">
                <span className="nv-grip" title="Drag to reorder" onMouseDown={() => setArmed(i)} onMouseUp={() => setArmed(null)}><Icon name="grip" size={14} /></span>
                <span className="cl-order">{i + 1}</span>
                <input className="nv-input cl-name" value={t.name} aria-label="Type name" maxLength={40}
                  onChange={(e) => edit(i, { name: e.target.value })} />
                <button className="nv-link" disabled={!n || !savedIds.has(t.id)} onClick={() => setOpen(open === t.id ? null : t.id)}
                  title={savedIds.has(t.id) ? "Show these files" : "Save to list this type's files"}>
                  {n === undefined ? "…" : `${n.toLocaleString()} file${n === 1 ? "" : "s"}`}
                </button>
                <span className="nv-spacer" />
                <button className="nv-iconbtn" title={`Move ${t.name} up`} disabled={i === 0} onClick={() => setTypes((x) => moved(x, i, i - 1))}><Icon name="up" size={14} /></button>
                <button className="nv-iconbtn" title={`Move ${t.name} down`} disabled={i === types.length - 1} onClick={() => setTypes((x) => moved(x, i, i + 1))}><Icon name="down" size={14} /></button>
                <button className="nv-iconbtn" title={`Remove ${t.name}`} onClick={() => setTypes((x) => x.filter((_, j) => j !== i))}><Icon name="close" size={14} /></button>
              </div>
              <Words words={t.words} onChange={(words) => edit(i, { words })} name={t.name} />
              {open === t.id && <Files typeId={t.id} dirty={dirty} />}
            </li>
          );
        })}
      </ol>
      <button className="nv-btn" onClick={() => setTypes((t) => [...t, { id: "", name: newName(t), words: [] }])}>
        <Icon name="plus" /> Add a type
      </button>

      <fieldset className="cl-fields">
        <legend>Look for the words in</legend>
        {page.available.map((f) => (
          <label key={f}>
            <input type="checkbox" checked={fields.includes(f)}
              onChange={(e) => setFields((x) => (e.target.checked ? page.available.filter((a) => a === f || x.includes(a)) : x.filter((a) => a !== f)))} />
            {FIELD_LABEL[f] ?? f}
          </label>
        ))}
      </fieldset>

      {page.columnLabel && (
        <p className="nv-dim cl-note">
          A file the words get wrong: set its <b>{page.columnLabel}</b> column (Songs list, details panel, or right-click) to a
          type's name, or to <b>{page.song}</b> to keep it a song. That beats the words.
        </p>
      )}

      {error && <div className="nv-warn bad">{error}</div>}
      <div className="cl-foot">
        <button className="nv-btn" disabled={busy || atDefaults} onClick={() => void reset()}>Reset to defaults</button>
        <span className="nv-spacer" />
        {dirty && <span className="nv-dim">Not saved yet</span>}
        <button className="nv-btn" disabled={!dirty || busy} onClick={() => take(page)}>Undo changes</button>
        <button className="nv-btn primary" disabled={!dirty || busy || !!error} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function newName(types: ClipType[]) {
  let n = 1;
  while (types.some((t) => t.name === `New type${n > 1 ? ` ${n}` : ""}`)) n += 1;
  return `New type${n > 1 ? ` ${n}` : ""}`;
}

function Words({ words, onChange, name }: { words: string[]; onChange: (w: string[]) => void; name: string }) {
  const [text, setText] = useState("");
  const add = () => {
    const word = text.trim().replace(/\s+/g, " ").toLowerCase();
    if (word && !words.includes(word)) onChange([...words, word]);
    setText("");
  };
  return (
    <div className="cl-words">
      {words.map((w) => (
        <span key={w} className="cl-word">
          {w}
          <button title={`Remove “${w}”`} onClick={() => onChange(words.filter((x) => x !== w))}>×</button>
        </span>
      ))}
      {words.length === 0 && <span className="nv-dim cl-none">No words: only files set by hand are {name}.</span>}
      <input
        className="cl-addword"
        value={text}
        placeholder="add a word or phrase"
        aria-label={`Add a word for ${name}`}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !text && words.length) onChange(words.slice(0, -1));
        }}
        onBlur={add}
      />
    </div>
  );
}

function Files({ typeId, dirty }: { typeId: string; dirty: boolean }) {
  const app = useApp();
  const [files, setFiles] = useState<Clip[] | null>(null);
  useEffect(() => {
    void call<{ clips: Clip[] }>("classify", { type: typeId }).then((r) => setFiles(r.clips));
  }, [typeId]);
  const ids = useMemo(() => (files ?? []).map((f) => f.trackId), [files]);
  if (!files) return <div className="cl-files nv-dim">Reading…</div>;
  return (
    <div className="cl-files">
      {dirty && <p className="nv-dim">These are the files for the saved types. Save to see your changes here.</p>}
      <ul>
        {files.slice(0, 50).map((f) => (
          <li key={f.trackId}>
            <span className="cl-file-title">{f.title}</span>
            <span className="nv-dim">{f.duration ? clockTime(f.duration) : ""}</span>
            <span className={`cl-why ${f.byHand ? "hand" : ""}`}>{f.why}</span>
          </li>
        ))}
      </ul>
      {files.length > 50 && <p className="nv-dim">…and {files.length - 50} more.</p>}
      {files.length > 0 && (
        <button className="nv-link" onClick={() => { app.closeSettings(); app.navigate("studio", { ids: ids.join(",") }); }}>
          Open {files.length === 1 ? "it" : `these ${files.length}`} in the tag editor
        </button>
      )}
    </div>
  );
}
